import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { portalApi, fmtUSD } from "../lib/api";
import { Button } from "../components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Building2, LogOut, Download, Printer, FileText, FileBarChart2, Bookmark, Trash2, Plus, PenSquare } from "lucide-react";
import ReportView from "../components/reports/ReportView";
import CustomReportBuilder from "../components/reports/CustomReportBuilder";
import EconDashboards from "../components/econ/EconDashboards";
import { toast } from "sonner";

function lastMonths(n = 6) {
  const out = [];
  const d = new Date();
  for (let i = 0; i < n; i++) {
    out.push(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`);
    d.setMonth(d.getMonth() - 1);
  }
  return out;
}

const STATE_CLS = {
  issued: "bg-[#dcfce7] text-[#15803d]",
  paid: "bg-[#bbf7d0] text-[#166534]",
  credited: "bg-[#fee2e2] text-[#b91c1c]",
};

export default function Portal() {
  const navigate = useNavigate();
  const [me, setMe] = useState(null);
  const [tab, setTab] = useState("invoices");
  const [invoices, setInvoices] = useState([]);
  const [detail, setDetail] = useState(null);
  const [period, setPeriod] = useState(lastMonths(1)[0]);
  const [usage, setUsage] = useState(null);
  const [reportsCatalog, setReportsCatalog] = useState([]);
  const [selectedReport, setSelectedReport] = useState(null);
  const [reportData, setReportData] = useState(null);
  const [myReports, setMyReports] = useState({ saved: [], recent: [] });
  const [customReports, setCustomReports] = useState([]);
  const periods = useMemo(() => lastMonths(6), []);

  useEffect(() => {
    portalApi.me().then(setMe).catch(() => navigate("/portal/login"));
  }, [navigate]);

  useEffect(() => {
    if (me) portalApi.invoices().then(setInvoices).catch(console.error);
  }, [me]);

  useEffect(() => {
    if (me && tab === "usage") portalApi.usage(period).then(setUsage).catch(console.error);
    if (me && tab === "reports") {
      portalApi.reportsCatalog().then((c) => {
        setReportsCatalog(c);
        if (!selectedReport && c.length) setSelectedReport(c[0].id);
      }).catch(console.error);
      portalApi.reportsMy().then(setMyReports).catch(console.error);
      portalApi.customList().then(setCustomReports).catch(console.error);
    }
    // eslint-disable-next-line
  }, [me, tab, period]);

  useEffect(() => {
    if (me && tab === "reports" && selectedReport) {
      if (selectedReport === "__builder") { setReportData(null); return; }
      setReportData(null);
      const p = selectedReport.startsWith("custom:")
        ? portalApi.customRun(selectedReport.slice(7))
        : portalApi.reportsRun(selectedReport);
      p.then(setReportData).catch(console.error);
    }
    // eslint-disable-next-line
  }, [me, tab, selectedReport]);

  const saveReport = async () => {
    try {
      await portalApi.reportsSave(selectedReport, reportData.name);
      toast.success("Saved to My Reports");
      setMyReports(await portalApi.reportsMy());
    } catch (e) {
      toast.error("Save failed", { description: e?.response?.data?.detail || e.message });
    }
  };

  const logout = async () => {
    try { await portalApi.logout(); } catch { /* ignore */ }
    navigate("/portal/login");
  };

  if (!me) {
    return <div className="min-h-screen flex items-center justify-center text-sm text-muted-foreground" data-testid="portal-checking">Loading portal…</div>;
  }

  const brand = me.branding?.primary || "#00579e";

  return (
    <div className="min-h-screen bg-[#f4f7fa]" data-testid="portal-page">
      {/* White-labeled header */}
      <header className="text-white" style={{ backgroundColor: brand }}>
        <div className="max-w-5xl mx-auto px-6 py-4 flex items-center gap-3">
          <div className="w-8 h-8 rounded-sm bg-white/15 flex items-center justify-center">
            <Building2 className="w-4 h-4" />
          </div>
          <div>
            <div className="cs-heading text-sm font-semibold leading-tight" data-testid="portal-msp-name">{me.branding?.msp_name}</div>
            <div className="text-[10px] uppercase tracking-[0.18em] text-white/70">Customer Portal</div>
          </div>
          <div className="ml-auto text-right">
            <div className="text-xs font-semibold" data-testid="portal-customer-name">{me.customer?.name}</div>
            <div className="text-[10px] text-white/70">{me.email}</div>
          </div>
          <button onClick={logout} data-testid="portal-logout-button"
            className="ml-4 flex items-center gap-1.5 text-[11px] px-3 py-1.5 rounded-full bg-white/10 hover:bg-white/20">
            <LogOut className="w-3.5 h-3.5" /> Sign out
          </button>
        </div>
      </header>

      <main className={`${tab === "dashboards" ? "max-w-7xl" : "max-w-5xl"} mx-auto px-6 py-8`}>
        <div className="flex items-center gap-2 mb-6">
          {[{ v: "invoices", l: "Invoices" }, { v: "dashboards", l: "Dashboards" }, { v: "usage", l: "Usage report" }, { v: "reports", l: "Reports" }].map((t) => (
            <button key={t.v} onClick={() => setTab(t.v)} data-testid={`portal-tab-${t.v}`}
              className={`px-4 py-1.5 rounded-full text-xs font-medium border ${tab === t.v ? "text-white border-transparent" : "bg-white border-border text-foreground"}`}
              style={tab === t.v ? { backgroundColor: brand } : {}}>
              {t.l}
            </button>
          ))}
          {tab === "usage" && (
            <div className="ml-auto flex items-center gap-2">
              <span className="text-[10px] uppercase tracking-[0.14em] text-muted-foreground">Period</span>
              <Select value={period} onValueChange={setPeriod}>
                <SelectTrigger className="rounded-sm h-8 text-xs w-[120px] cs-mono bg-white" data-testid="portal-period-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>{periods.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          )}
        </div>

        {tab === "dashboards" && (
          <div data-testid="portal-dashboards-tab">
            <EconDashboards client={portalApi.econ} />
          </div>
        )}

        {tab === "invoices" && (
          <div className="cs-card overflow-hidden" data-testid="portal-invoices-table">
            <table className="w-full text-sm">
              <thead className="bg-[hsl(var(--cs-tint))] border-b border-border">
                <tr className="text-left">
                  {["Number", "Period", "State", "Amount", "Issued", "Actions"].map((h) => (
                    <th key={h} className="px-4 py-3 text-[10px] uppercase tracking-[0.14em] font-medium text-muted-foreground">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {invoices.length === 0 && (
                  <tr><td colSpan={6} className="px-4 py-10 text-center text-muted-foreground" data-testid="portal-no-invoices">
                    No issued invoices yet — your MSP will publish them here.
                  </td></tr>
                )}
                {invoices.map((i) => (
                  <tr key={i.id} className="border-b border-border last:border-0" data-testid={`portal-invoice-${i.number}`}>
                    <td className="px-4 py-3 cs-mono text-[12px] font-semibold">
                      {i.number}
                      {i.doc_type === "credit_note" && <span className="ml-1 text-[10px] text-[#b91c1c]">(credit note)</span>}
                    </td>
                    <td className="px-4 py-3 cs-mono text-xs">{i.period}</td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-0.5 rounded-sm text-[10px] font-semibold uppercase ${STATE_CLS[i.state] || "bg-secondary"}`}>{i.state}</span>
                    </td>
                    <td className="px-4 py-3 text-right cs-mono font-semibold">{i.totals?.billed_amount?.toLocaleString()} {i.totals?.currency}</td>
                    <td className="px-4 py-3 text-xs text-muted-foreground">{(i.issued_at || i.created_at || "").slice(0, 10)}</td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <button onClick={() => portalApi.invoice(i.id).then(setDetail)} data-testid={`portal-view-${i.number}`}
                        className="inline-flex items-center gap-1 text-[11px] font-semibold hover:underline" style={{ color: brand }}>
                        <FileText className="w-3.5 h-3.5" /> View
                      </button>
                      <a href={portalApi.exportUrl(i.id)} className="inline-flex items-center gap-1 text-[11px] text-muted-foreground hover:underline ml-3"
                         data-testid={`portal-csv-${i.number}`}>
                        <Download className="w-3.5 h-3.5" /> CSV
                      </a>
                      <a href={`/portal/invoice/${i.id}`} target="_blank" rel="noreferrer"
                         className="inline-flex items-center gap-1 text-[11px] text-muted-foreground hover:underline ml-3"
                         data-testid={`portal-print-${i.number}`}>
                        <Printer className="w-3.5 h-3.5" /> Print
                      </a>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {tab === "usage" && (
          <div className="cs-card overflow-hidden" data-testid="portal-usage-table">
            <table className="w-full text-sm">
              <thead className="bg-[hsl(var(--cs-tint))] border-b border-border">
                <tr className="text-left">
                  {["Service", "Category", "Region", "Usage", "Amount (USD)"].map((h) => (
                    <th key={h} className="px-4 py-3 text-[10px] uppercase tracking-[0.14em] font-medium text-muted-foreground">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {(usage?.lines || []).map((l, idx) => (
                  <tr key={idx} className="border-b border-border last:border-0">
                    <td className="px-4 py-3 font-medium">{l.service}</td>
                    <td className="px-4 py-3 text-muted-foreground text-xs">{l.service_category}</td>
                    <td className="px-4 py-3 cs-mono text-xs">{l.region}</td>
                    <td className="px-4 py-3 text-right cs-mono text-xs">{Number(l.usage_qty).toLocaleString()} {l.unit}</td>
                    <td className="px-4 py-3 text-right cs-mono font-semibold">{fmtUSD(l.amount_usd)}</td>
                  </tr>
                ))}
              </tbody>
              {usage && (
                <tfoot>
                  <tr className="border-t-2 border-border font-semibold">
                    <td colSpan={4} className="px-4 py-3 text-right">TOTAL — {usage.period}</td>
                    <td className="px-4 py-3 text-right cs-mono" data-testid="portal-usage-total">{fmtUSD(usage.total_usd)}</td>
                  </tr>
                </tfoot>
              )}
            </table>
          </div>
        )}
        {tab === "reports" && (
          <div className="flex gap-5" data-testid="portal-reports-tab">
            <aside className="w-[230px] shrink-0 space-y-3">
              <button onClick={() => setSelectedReport("__builder")} data-testid="portal-create-report-button"
                className="w-full rounded-sm h-9 text-xs text-white flex items-center justify-center gap-1.5"
                style={{ backgroundColor: brand }}>
                <Plus className="w-4 h-4" /> Create report
              </button>
              <div className="cs-card p-3">
                <div className="text-[10px] uppercase tracking-[0.16em] text-muted-foreground mb-1.5">Your reports</div>
                {reportsCatalog.map((r) => (
                  <button key={r.id} onClick={() => setSelectedReport(r.id)} data-testid={`portal-report-${r.id}`}
                    className={`w-full text-left text-[12px] px-2 py-1.5 rounded-sm flex items-center gap-1.5 ${selectedReport === r.id ? "text-white font-medium" : "hover:bg-[hsl(var(--cs-tint))]"}`}
                    style={selectedReport === r.id ? { backgroundColor: brand } : {}}>
                    <FileBarChart2 className="w-3 h-3 shrink-0 opacity-70" /> {r.name}
                  </button>
                ))}
              </div>
              {customReports.length > 0 && (
                <div className="cs-card p-3" data-testid="portal-custom-reports">
                  <div className="text-[10px] uppercase tracking-[0.16em] text-muted-foreground mb-1.5">Custom reports</div>
                  {customReports.map((c) => (
                    <div key={c.id} className="flex items-center gap-1.5 py-1 group">
                      <PenSquare className="w-3 h-3 shrink-0" style={{ color: brand }} />
                      <button onClick={() => setSelectedReport(`custom:${c.id}`)} data-testid={`portal-custom-${c.id}`}
                        className="text-[11px] text-left truncate flex-1 hover:underline">{c.name}</button>
                      <button onClick={async () => {
                        await portalApi.customDelete(c.id);
                        setCustomReports(await portalApi.customList());
                        if (selectedReport === `custom:${c.id}`) setSelectedReport(reportsCatalog[0]?.id);
                      }} data-testid={`portal-delete-custom-${c.id}`} className="opacity-0 group-hover:opacity-100">
                        <Trash2 className="w-3 h-3 text-[#b91c1c]" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
              {myReports.saved.length > 0 && (
                <div className="cs-card p-3" data-testid="portal-my-reports">
                  <div className="text-[10px] uppercase tracking-[0.16em] text-muted-foreground mb-1.5">Saved</div>
                  {myReports.saved.map((s) => (
                    <div key={s.id} className="flex items-center gap-1.5 py-1 group">
                      <Bookmark className="w-3 h-3 shrink-0" style={{ color: brand }} />
                      <button onClick={() => setSelectedReport(s.report_id)} data-testid={`portal-saved-${s.id}`}
                        className="text-[11px] text-left truncate flex-1 hover:underline">{s.name}</button>
                      <button onClick={async () => { await portalApi.reportsDeleteSaved(s.id); setMyReports(await portalApi.reportsMy()); }}
                        data-testid={`portal-delete-saved-${s.id}`} className="opacity-0 group-hover:opacity-100">
                        <Trash2 className="w-3 h-3 text-[#b91c1c]" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </aside>
            <div className="flex-1 min-w-0">
              {selectedReport === "__builder" ? (
                <CustomReportBuilder
                  accent={brand}
                  api={{ fields: portalApi.customFields, preview: portalApi.customPreview, save: portalApi.customSave }}
                  onSaved={async (doc) => { setCustomReports(await portalApi.customList()); setSelectedReport(`custom:${doc.id}`); }}
                />
              ) : reportData
                ? <ReportView data={reportData} onSave={selectedReport.startsWith("custom:") ? undefined : saveReport} />
                : <div className="text-sm text-muted-foreground py-8" data-testid="portal-report-loading">Running report…</div>}
            </div>
          </div>
        )}

      </main>

      {/* Invoice detail dialog (sanitized — customer prices only) */}
      <Dialog open={!!detail} onOpenChange={(v) => !v && setDetail(null)}>
        <DialogContent className="max-w-3xl rounded-sm border-border max-h-[85vh] overflow-y-auto" data-testid="portal-invoice-modal">
          {detail && (
            <>
              <DialogHeader>
                <DialogTitle className="cs-heading text-lg">{detail.number}</DialogTitle>
                <DialogDescription>
                  {detail.period} · {detail.state} · 1 USD = {detail.fx?.rate} {detail.fx?.currency} ({detail.fx?.source})
                </DialogDescription>
              </DialogHeader>
              <table className="w-full text-xs">
                <thead className="bg-[hsl(var(--cs-tint))] border-b border-border">
                  <tr className="text-left">
                    {["Description", "Qty", "Unit rate (USD)", `Amount (${detail.fx?.currency})`].map((h) => (
                      <th key={h} className="px-3 py-2 text-[10px] uppercase tracking-[0.12em] text-muted-foreground">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {detail.lines.map((l, idx) => (
                    <tr key={idx} className="border-b border-border last:border-0">
                      <td className="px-3 py-2">{l.description}{l.formula && <div className="text-[10px] text-muted-foreground cs-mono">{l.formula}</div>}</td>
                      <td className="px-3 py-2 text-right cs-mono">{Number(l.qty).toLocaleString()}</td>
                      <td className="px-3 py-2 text-right cs-mono">{Number(l.unit_rate).toLocaleString(undefined, { maximumFractionDigits: 4 })}</td>
                      <td className={`px-3 py-2 text-right cs-mono ${l.amount < 0 ? "text-[#b91c1c]" : ""}`}>
                        {(l.amount * detail.fx.rate).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="border-t-2 border-border font-semibold">
                    <td colSpan={3} className="px-3 py-2 text-right">TOTAL DUE</td>
                    <td className="px-3 py-2 text-right cs-mono" data-testid="portal-modal-total">
                      {detail.totals.billed_amount?.toLocaleString()} {detail.totals.currency}
                    </td>
                  </tr>
                </tfoot>
              </table>
              <div className="flex justify-end gap-2">
                <a href={portalApi.exportUrl(detail.id)}>
                  <Button variant="outline" className="rounded-full h-8 text-xs"><Download className="w-3.5 h-3.5 mr-1.5" /> Reconciling report (CSV)</Button>
                </a>
                <a href={`/portal/invoice/${detail.id}`} target="_blank" rel="noreferrer">
                  <Button className="rounded-full h-8 text-xs text-white" style={{ backgroundColor: brand }}>
                    <Printer className="w-3.5 h-3.5 mr-1.5" /> Print / PDF
                  </Button>
                </a>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
